
  // QuickStream Trusted Frame: QuickstreamAPI.init -> creditCards.createTrustedFrame
  // -> submitForm -> data.singleUseToken.singleUseTokenId. Research note, section 2.
  //
  // Remount case: same as .PayWayCardFrame above — the payer/staff pages
  // remove this container on a decline and re-render it on "Try again",
  // so `mounted()` runs again. The QuickStream script must never be
  // appended twice, so start() is only ever reached one of three ways:
  // (1) window.QuickstreamAPI already exists -> call start() straight
  // away; (2) a <script> with this exact src is already in <head> (a
  // prior mount's) -> ride its load/error events, or call start()
  // directly if it already finished (its data-loaded marker); (3)
  // neither exists -> append the one script and mark it data-loaded on
  // load, so a later remount takes branch (2)'s already-loaded path.
  export default {
    mounted() {
      const { publishableKey, scriptUrl, merchant } = this.el.dataset;
      const mountId = `${this.el.id}-mount`;
      const fail = (message) => this.pushEvent("card_frame_error", {message});
      const start = () => {
        // The SDK chooses its mount point by SEARCHING THE DOCUMENT for
        // this attribute — there is no selector option to pass, so the
        // container has to be marked before createTrustedFrame runs.
        // Set here rather than in the shared markup above because the
        // attribute is QuickStream's alone; the dummy and PayWay hooks
        // mount into the same div by other means.
        document.getElementById(mountId).setAttribute("data-quickstream-api", "creditCardContainer");
        window.QuickstreamAPI.init({publishableApiKey: publishableKey});
        window.QuickstreamAPI.creditCards.createTrustedFrame({
          config: {supplierBusinessCode: merchant},
          // IframeObject's own width/height are INTS (defaults 800x300);
          // a percentage is only legal under iframe.style, which is a
          // StyleObject and does support `width`.
          // The frame never grows to fit its content, so the height is
          // ours to set: the default 300 cut the CVN off on the first real
          // sandbox payment. 480 leaves room for a message under a field.
          iframe: {style: {width: "100%"}, height: 480},
          showAcceptedCards: true
        }, (errors, data) => {
          if (errors) { fail("The card form could not be loaded"); return; }
          this.frame = data.trustedFrame;
        });
      };
      if (window.QuickstreamAPI) {
        start();
      } else {
        const existing = document.head.querySelector(`script[src="${scriptUrl}"]`);
        if (existing) {
          if (existing.dataset.loaded) {
            start();
          } else {
            existing.addEventListener("load", start);
            existing.addEventListener("error", () => fail("The card form could not be loaded"));
          }
        } else {
          const script = document.createElement("script");
          script.src = scriptUrl;
          script.onload = () => { script.dataset.loaded = "true"; start(); };
          // Remove the failed tag rather than leaving it in <head> with
          // no data-loaded marker — otherwise a LATER remount's
          // `existing` branch finds it, attaches listeners to an
          // element whose load/error events already fired, and never
          // calls start() or fail() again (stuck on "still loading").
          script.onerror = () => { script.remove(); fail("The card form could not be loaded"); };
          document.head.appendChild(script);
        }
      }
      this.el.querySelector(`#${this.el.id}-pay`).addEventListener("click", () => {
        if (!this.frame) { fail("The card form is still loading"); return; }
        this.frame.submitForm((errors, data) => {
          if (errors) { fail((errors[0] && errors[0].messages && errors[0].messages[0]) || "Check the card details"); return; }
          this.pushEvent("card_tokenised", {token: data.singleUseToken.singleUseTokenId});
        });
      });
    },
    // teardown takes a callback in the reference; pass one rather than
    // relying on it being optional.
    destroyed() { if (this.frame && this.frame.teardown) this.frame.teardown(() => {}); }
  }
